import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { type RootState } from '@/store';
import { PaginationState, SortingState } from '@tanstack/react-table';
import { CaseData, CaseFileData, CaseFilesSelectedFilters } from '@/api';
import { readMockAudioFilesEnabled, withMockAudioFiles } from './mockAudioCaseFile';

interface CasesListState {
  casesList: CaseData[];
  casesRowCount: number;
  casesListPagination: PaginationState;
  casesListSorting: SortingState;
  casesListFilters: {
    departments: string[];
    clients: string[];
    defendants: string[];
    fileHandlers: string[];
    teams: string[];
    sources: string[];
  };
  caseFilesList: CaseFileData[];
  caseFilesLoadedForCaseId: string | null;
  /** Temporary QA toggle for the mock audio rows. */
  showMockAudioFiles: boolean;
  transcribingFiles: Record<string, number>;
  transcribingFilesCaseId: string | null;
  caseFilesRowCount: number;
  caseFilesPagination: PaginationState;
  caseFilesSorting: SortingState;
  caseFilesFilters: CaseFilesSelectedFilters;
  caseFilesSearchTerm: string;
  /** The debounced search term the current file list was requested with. */
  caseFilesAppliedSearchTerm: string;
  /** Case the filters above belong to, or `null` before they're restored. The files request waits for it. */
  caseFilesFiltersCaseId: string | null;
  caseFilesAvailableFilterOptions: Record<string, string[]>;
  caseFilesCategoryFilterOptions: Record<string, { label: string; id: string }[]>;
  caseFilesAvailableMinDate: string | null;
  caseFilesAvailableMaxDate: string | null;
  caseFilesSelectedMinDate: string | null;
  caseFilesSelectedMaxDate: string | null;
}

const initialState: CasesListState = {
  casesList: [],
  casesRowCount: 0,
  casesListPagination: {
    pageIndex: 0,
    pageSize: 50,
  },
  casesListSorting: [
    {
      id: 'lastSyncedDatetime',
      desc: true,
    },
  ],
  casesListFilters: {
    departments: [],
    clients: [],
    defendants: [],
    fileHandlers: [],
    teams: [],
    sources: [],
  },
  caseFilesList: [],
  caseFilesLoadedForCaseId: null,
  showMockAudioFiles: readMockAudioFilesEnabled(),
  transcribingFiles: {},
  transcribingFilesCaseId: null,
  caseFilesRowCount: 0,
  caseFilesPagination: {
    pageIndex: 0,
    pageSize: 50,
  },
  caseFilesSorting: [
    {
      id: 'uploadedAt',
      desc: false,
    },
  ],
  caseFilesFilters: {},
  caseFilesSearchTerm: '',
  caseFilesAppliedSearchTerm: '',
  caseFilesFiltersCaseId: null,
  caseFilesAvailableFilterOptions: {},
  caseFilesCategoryFilterOptions: {},
  caseFilesAvailableMinDate: null,
  caseFilesAvailableMaxDate: null,
  caseFilesSelectedMinDate: null,
  caseFilesSelectedMaxDate: null,
};

const casesListSlice = createSlice({
  name: 'cases',
  initialState: initialState,
  reducers: {
    setCasesList: (state, action: PayloadAction<CaseData[]>) => {
      state.casesList = action.payload;
    },
    setCasesRowCount: (state, action: PayloadAction<number>) => {
      state.casesRowCount = action.payload;
    },
    setCasesPagination: (state, action: PayloadAction<PaginationState>) => {
      state.casesListPagination = action.payload;
    },
    setCasesSorting: (state, action: PayloadAction<SortingState>) => {
      state.casesListSorting = action.payload;
    },
    setCasesFilters: (
      state,
      action: PayloadAction<{
        departments?: string[];
        clients?: string[];
        defendants?: string[];
        fileHandlers?: string[];
        teams?: string[];
        sources?: string[];
      }>,
    ) => {
      if (action.payload.departments !== undefined) {
        state.casesListFilters.departments = action.payload.departments;
      }
      if (action.payload.clients !== undefined) {
        state.casesListFilters.clients = action.payload.clients;
      }
      if (action.payload.defendants !== undefined) {
        state.casesListFilters.defendants = action.payload.defendants;
      }
      if (action.payload.fileHandlers !== undefined) {
        state.casesListFilters.fileHandlers = action.payload.fileHandlers;
      }
      if (action.payload.teams !== undefined) {
        state.casesListFilters.teams = action.payload.teams;
      }
      if (action.payload.sources !== undefined) {
        state.casesListFilters.sources = action.payload.sources;
      }
    },
    clearCasesFilters: (state) => {
      state.casesListFilters = {
        departments: [],
        clients: [],
        defendants: [],
        fileHandlers: [],
        teams: [],
        sources: [],
      };
    },
    setCaseFilesList: (state, action: PayloadAction<{ caseId: string; files: CaseFileData[] }>) => {
      state.caseFilesList = withMockAudioFiles(action.payload.files, state.showMockAudioFiles);
      state.caseFilesLoadedForCaseId = action.payload.caseId;
    },
    /** Removes deleted rows right away instead of waiting for the refetch. Ignored for other cases. */
    removeCaseFiles: (state, action: PayloadAction<{ caseId: string; historyRowIds: string[] }>) => {
      if (state.caseFilesLoadedForCaseId !== action.payload.caseId) return;
      const removedIds = new Set(action.payload.historyRowIds);
      if (removedIds.size === 0) return;

      const remaining = state.caseFilesList.filter((file) => !removedIds.has(file.historyRowId));
      state.caseFilesRowCount = Math.max(0, state.caseFilesRowCount - (state.caseFilesList.length - remaining.length));
      state.caseFilesList = remaining.map((file) =>
        file.attachments?.some((attachment) => removedIds.has(attachment.historyRowId))
          ? { ...file, attachments: file.attachments.filter((a) => !removedIds.has(a.historyRowId)) }
          : file,
      );
    },
    // Temporary: re-applied to the loaded list so the toggle takes effect immediately.
    setShowMockAudioFiles: (state, action: PayloadAction<boolean>) => {
      state.showMockAudioFiles = action.payload;
      state.caseFilesList = withMockAudioFiles(state.caseFilesList, action.payload);
    },
    /**
     * Restores remembered transcription requests for `caseId`. Ignored once hydrated, so both
     * consumers can call it on mount.
     */
    hydrateTranscribingFiles: (state, action: PayloadAction<{ caseId: string; files: Record<string, number> }>) => {
      if (state.transcribingFilesCaseId === action.payload.caseId) return;
      state.transcribingFilesCaseId = action.payload.caseId;
      state.transcribingFiles = action.payload.files;
    },
    setTranscribingFiles: (state, action: PayloadAction<{ caseId: string; files: Record<string, number> }>) => {
      state.transcribingFilesCaseId = action.payload.caseId;
      state.transcribingFiles = action.payload.files;
    },
    /** Drops finished requests. Ignored unless `caseId` is the case whose requests are loaded. */
    removeTranscribingFiles: (state, action: PayloadAction<{ caseId: string; documentIds: string[] }>) => {
      if (state.transcribingFilesCaseId !== action.payload.caseId) return;
      action.payload.documentIds.forEach((documentId) => {
        delete state.transcribingFiles[documentId];
      });
    },
    setCaseFilesRowCount: (state, action: PayloadAction<number>) => {
      state.caseFilesRowCount = action.payload;
    },
    setCaseFilesPagination: (state, action: PayloadAction<PaginationState>) => {
      state.caseFilesPagination = action.payload;
    },
    setCaseFilesSorting: (state, action: PayloadAction<SortingState>) => {
      state.caseFilesSorting = action.payload;
    },
    setCaseFilesFilters: (state, action: PayloadAction<CaseFilesSelectedFilters>) => {
      state.caseFilesFilters = action.payload;
    },
    clearCaseFilesFilters: (state) => {
      state.caseFilesFilters = {};
    },
    setCaseFilesSearchTerm: (state, action: PayloadAction<string>) => {
      state.caseFilesSearchTerm = action.payload;
    },
    setCaseFilesAppliedSearchTerm: (state, action: PayloadAction<string>) => {
      state.caseFilesAppliedSearchTerm = action.payload;
    },
    /** Restores the filters kept for `caseId` this session (or the empty state when none were). */
    hydrateCaseFilesFilters: (
      state,
      action: PayloadAction<{
        caseId: string;
        filters: CaseFilesSelectedFilters;
        minDate: string | null;
        maxDate: string | null;
        searchTerm: string;
      }>,
    ) => {
      state.caseFilesFiltersCaseId = action.payload.caseId;
      state.caseFilesFilters = action.payload.filters;
      state.caseFilesSelectedMinDate = action.payload.minDate;
      state.caseFilesSelectedMaxDate = action.payload.maxDate;
      state.caseFilesSearchTerm = action.payload.searchTerm;
      // Restored in one go with the filters, so the file list is requested once, already
      // narrowed down, instead of loading unfiltered first and then debouncing.
      state.caseFilesAppliedSearchTerm = action.payload.searchTerm;
    },
    /** Clears in-memory filters when leaving the case page. Session storage is kept. */
    resetCaseFilesFilterState: (state) => {
      state.caseFilesFiltersCaseId = null;
      state.caseFilesFilters = {};
      state.caseFilesSelectedMinDate = null;
      state.caseFilesSelectedMaxDate = null;
      state.caseFilesSearchTerm = '';
      state.caseFilesAppliedSearchTerm = '';
    },
    setCaseFilesSelectedDateRange: (
      state,
      action: PayloadAction<{ minDate: string | null; maxDate: string | null }>,
    ) => {
      state.caseFilesSelectedMinDate = action.payload.minDate;
      state.caseFilesSelectedMaxDate = action.payload.maxDate;
    },
    clearCaseFilesSelectedDateRange: (state) => {
      state.caseFilesSelectedMinDate = null;
      state.caseFilesSelectedMaxDate = null;
    },
    setCaseFilesAvailableFilterOptions: (state, action: PayloadAction<Record<string, string[]>>) => {
      state.caseFilesAvailableFilterOptions = action.payload;
    },
    setCaseFilesCategoryFilterOptions: (
      state,
      action: PayloadAction<Record<string, { label: string; id: string }[]>>,
    ) => {
      state.caseFilesCategoryFilterOptions = action.payload;
    },
    setCaseFilesAvailableDateBounds: (
      state,
      action: PayloadAction<{ minDate: string | null; maxDate: string | null }>,
    ) => {
      state.caseFilesAvailableMinDate = action.payload.minDate;
      state.caseFilesAvailableMaxDate = action.payload.maxDate;
    },
  },
});

export const {
  setCasesList,
  setCasesRowCount,
  setCasesPagination,
  setCasesSorting,
  setCasesFilters,
  clearCasesFilters,
  setCaseFilesList,
  removeCaseFiles,
  setShowMockAudioFiles,
  hydrateTranscribingFiles,
  setTranscribingFiles,
  removeTranscribingFiles,
  setCaseFilesRowCount,
  setCaseFilesPagination,
  setCaseFilesSorting,
  setCaseFilesFilters,
  clearCaseFilesFilters,
  setCaseFilesSearchTerm,
  setCaseFilesAppliedSearchTerm,
  hydrateCaseFilesFilters,
  resetCaseFilesFilterState,
  setCaseFilesSelectedDateRange,
  clearCaseFilesSelectedDateRange,
  setCaseFilesAvailableFilterOptions,
  setCaseFilesCategoryFilterOptions,
  setCaseFilesAvailableDateBounds,
} = casesListSlice.actions;

export const selectCasesList = (state: RootState) => {
  return state.cases.casesList;
};
export const selectCasesRowCount = (state: RootState) => {
  return state.cases.casesRowCount;
};
export const selectCasesPagination = (state: RootState) => {
  return state.cases.casesListPagination;
};
export const selectCasesSorting = (state: RootState) => {
  return state.cases.casesListSorting;
};
export const selectCasesFilters = (state: RootState) => {
  return state.cases.casesListFilters;
};

export const selectCaseFilesList = (state: RootState) => {
  return state.cases.caseFilesList;
};
export const selectShowMockAudioFiles = (state: RootState) => {
  return state.cases.showMockAudioFiles;
};
export const selectTranscribingFiles = (state: RootState) => {
  return state.cases.transcribingFiles;
};
export const selectTranscribingFilesCaseId = (state: RootState) => {
  return state.cases.transcribingFilesCaseId;
};
export const selectCaseFilesLoadedForCaseId = (state: RootState) => {
  return state.cases.caseFilesLoadedForCaseId;
};
export const selectCaseFilesRowCount = (state: RootState) => {
  return state.cases.caseFilesRowCount;
};
export const selectCaseFilesPagination = (state: RootState) => {
  return state.cases.caseFilesPagination;
};
export const selectCaseFilesSorting = (state: RootState) => {
  return state.cases.caseFilesSorting;
};
export const selectCaseFilesFilters = (state: RootState) => {
  return state.cases.caseFilesFilters;
};
export const selectCaseFilesSearchTerm = (state: RootState) => {
  return state.cases.caseFilesSearchTerm;
};
export const selectCaseFilesAppliedSearchTerm = (state: RootState) => {
  return state.cases.caseFilesAppliedSearchTerm;
};
export const selectCaseFilesFiltersCaseId = (state: RootState) => {
  return state.cases.caseFilesFiltersCaseId;
};
export const selectCaseFilesAvailableFilterOptions = (state: RootState) => {
  return state.cases.caseFilesAvailableFilterOptions;
};
export const selectCaseFilesCategoryFilterOptions = (state: RootState) => {
  return state.cases.caseFilesCategoryFilterOptions;
};
export const selectCaseFilesAvailableMinDate = (state: RootState) => {
  return state.cases.caseFilesAvailableMinDate;
};
export const selectCaseFilesAvailableMaxDate = (state: RootState) => {
  return state.cases.caseFilesAvailableMaxDate;
};
export const selectCaseFilesSelectedMinDate = (state: RootState) => {
  return state.cases.caseFilesSelectedMinDate;
};
export const selectCaseFilesSelectedMaxDate = (state: RootState) => {
  return state.cases.caseFilesSelectedMaxDate;
};

export const casesListReducer = casesListSlice.reducer;
